/**
 * WireframeUploadDialog - Upload and Replace Wireframes/Designs Component
 * ========================================================================
 *
 * This component handles uploading .html or .zip files to replace wireframes.
 * Supports two modes:
 * - Replace specific page: Replace only the selected wireframe
 * - Replace all: Replace entire wireframe set with uploaded content
 *
 * Features:
 * - Link validation: Shows warnings for broken internal links
 * - Preview before commit: User can review validation results before confirming
 * - File type validation: Only accepts .html and .zip files
 *
 * This is an isolated feature that does NOT affect existing functionality.
 */

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
  Link as LinkIcon,
  LinkOff as BrokenLinkIcon,
  FolderZip as ZipIcon,
  Code as HtmlIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';

const WireframeUploadDialog = ({
  open,
  onClose,
  projectId,
  taskType = 'wireframes',
  pages = [],
  selectedPageId = null,
  onUploadComplete
}) => {
  // State for upload mode and file
  const [replaceType, setReplaceType] = useState(selectedPageId ? 'single' : 'all');
  const [selectedPage, setSelectedPage] = useState(selectedPageId || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // State for upload process
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setReplaceType(selectedPageId ? 'single' : 'all');
      setSelectedPage(selectedPageId || '');
      setSelectedFile(null);
      setValidationResult(null);
      setUploadError(null);
      setUploadSuccess(false);
    }
  }, [open, selectedPageId]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  // Validate file type and set
  const validateAndSetFile = (file) => {
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.html') && !filename.endsWith('.zip')) {
      setUploadError('Only .html and .zip files are accepted');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
    setValidationResult(null);
    setUploadSuccess(false);
  };

  // Clear selected file
  const handleClearFile = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate upload
  const handleValidate = async () => {
    if (!selectedFile) return;

    // Validation for single mode
    if (replaceType === 'single' && !selectedPage) {
      setUploadError('Please select a page to replace');
      return;
    }

    setIsValidating(true);
    setUploadError(null);
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('replace_type', replaceType);
      if (replaceType === 'single') {
        formData.append('page_id', selectedPage);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/visual-pages/projects/${projectId}/${taskType}/validate-upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok) {
        setValidationResult(data.data);
      } else {
        setUploadError(data.detail || 'Validation failed');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setUploadError(error.message || 'Failed to validate file');
    } finally {
      setIsValidating(false);
    }
  };

  // Perform actual upload
  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('replace_type', replaceType);
      if (replaceType === 'single') {
        formData.append('page_id', selectedPage);
      }
      formData.append('skip_validation', 'true'); // Skip validation since we already validated

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/visual-pages/projects/${projectId}/${taskType}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadSuccess(true);
        // Notify parent to refresh
        if (onUploadComplete) {
          onUploadComplete(data.data);
        }
        // Close dialog after short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setUploadError(data.detail || data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // Get file icon based on type
  const getFileIcon = () => {
    if (!selectedFile) return <UploadIcon />;
    return selectedFile.name.toLowerCase().endsWith('.zip') ? <ZipIcon /> : <HtmlIcon />;
  };

  // Render validation results
  const renderValidationResults = () => {
    if (!validationResult) return null;

    const { is_valid, warnings, errors, info } = validationResult;

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }} />

        {/* Errors */}
        {errors && errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Errors</AlertTitle>
            <List dense>
              {errors.map((error, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={error} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warnings</AlertTitle>
            <List dense>
              {warnings.map((warning, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={warning} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Broken Links Details */}
        {info?.broken_links && info.broken_links.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Broken Links Found</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The following links reference pages that don't exist:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {info.broken_links.map((link, idx) => (
                <Chip
                  key={idx}
                  icon={<BrokenLinkIcon />}
                  label={link.filename}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          </Alert>
        )}

        {/* ZIP File Contents */}
        {info?.html_files_in_zip && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>ZIP Contents ({info.total_files_in_zip} HTML files)</AlertTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {info.html_files_in_zip.map((filename, idx) => (
                <Chip
                  key={idx}
                  icon={<HtmlIcon />}
                  label={filename}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Alert>
        )}

        {/* Pages to be deleted */}
        {info?.pages_to_be_deleted && info.pages_to_be_deleted.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Pages to be Replaced ({info.pages_to_be_deleted.length})</AlertTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {info.pages_to_be_deleted.slice(0, 10).map((filename, idx) => (
                <Chip
                  key={idx}
                  icon={<DeleteIcon />}
                  label={filename}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
              {info.pages_to_be_deleted.length > 10 && (
                <Chip
                  label={`+${info.pages_to_be_deleted.length - 10} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Alert>
        )}

        {/* Success - Ready to upload */}
        {is_valid && (
          <Alert severity="success" icon={<CheckIcon />}>
            <AlertTitle>Validation Passed</AlertTitle>
            {warnings && warnings.length > 0
              ? 'File is valid but has warnings. You can proceed with the upload or cancel to make changes.'
              : 'File is valid and ready to upload.'}
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="primary" />
          <Typography variant="h6">
            Upload & Replace {taskType === 'wireframes' ? 'Wireframes' : taskType === 'designs' ? 'Designs' : 'Design System'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isUploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Success Message */}
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Upload Successful!</AlertTitle>
            {replaceType === 'single'
              ? `Successfully replaced ${selectedPage}.html`
              : 'Successfully replaced all wireframes'}
          </Alert>
        )}

        {/* Error Message */}
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        {/* Replacement Mode Selection */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
            Replacement Mode
          </FormLabel>
          <RadioGroup
            value={replaceType}
            onChange={(e) => {
              setReplaceType(e.target.value);
              setValidationResult(null);
            }}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Replace specific page</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only the selected page will be replaced. All other pages remain unchanged.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Replace ALL wireframes</Typography>
                  <Typography variant="caption" color="error">
                    All existing wireframes will be removed and replaced with the uploaded content.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Page Selection (for single mode) */}
        {replaceType === 'single' && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Select Page to Replace</FormLabel>
            <Select
              value={selectedPage}
              onChange={(e) => {
                setSelectedPage(e.target.value);
                setValidationResult(null);
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select a page...
              </MenuItem>
              {pages.map((page) => (
                <MenuItem key={page.page_id} value={page.page_id}>
                  {page.title} ({page.page_id}.html)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* File Upload Area */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Upload File</FormLabel>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: dragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.zip"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  {getFileIcon()}
                  <Typography variant="body1" fontWeight={500}>
                    {selectedFile.name}
                  </Typography>
                  <Chip
                    label={`${(selectedFile.size / 1024).toFixed(1)} KB`}
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearFile();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Click or drag to change file
                </Typography>
              </Box>
            ) : (
              <Box>
                <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Drag & drop a file here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepted formats: .html, .zip
                </Typography>
              </Box>
            )}
          </Box>
        </FormControl>

        {/* Validation Progress */}
        {isValidating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Validating file...</Typography>
          </Box>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Uploading...</Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Validation Results */}
        {renderValidationResults()}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {validationResult?.info && (
            <Typography variant="caption" color="text.secondary">
              {replaceType === 'all'
                ? `${validationResult.info.existing_pages_count || 0} existing pages will be replaced`
                : `Replacing: ${selectedPage}.html`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>

          {/* Validate Button */}
          {!validationResult && (
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={!selectedFile || isValidating || (replaceType === 'single' && !selectedPage)}
              startIcon={isValidating ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </Button>
          )}

          {/* Upload Button (shown after validation) */}
          {validationResult?.is_valid && (
            <Button
              variant="contained"
              color={validationResult.warnings?.length > 0 ? 'warning' : 'success'}
              onClick={handleUpload}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            >
              {isUploading ? 'Uploading...' : (validationResult.warnings?.length > 0 ? 'Upload Anyway' : 'Upload & Replace')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WireframeUploadDialog;
